import { mrkdwnBlocks } from '../../internals/mrkdwnBlocks';
import { mrkdwnSegments } from '../../internals/mrkdwnSegments';

import type { JSONContent } from '@tiptap/core';

function inline(text: string): JSONContent[] {
  return mrkdwnSegments(text).flatMap((segment): JSONContent[] => {
    if (segment.kind === 'mention') {
      return [
        {
          type: 'slackToken',
          attrs: { source: segment.source, label: `@${segment.name}` },
        },
      ];
    }
    if (segment.kind === 'channel')
      return [{ type: 'text', text: segment.source }];
    if (!segment.text) return [];
    if (segment.kind === 'text') return [{ type: 'text', text: segment.text }];
    const type = segment.kind;
    return [
      {
        type: 'text',
        text: segment.text,
        marks: [
          {
            type,
            ...(segment.kind === 'link'
              ? { attrs: { href: segment.href } }
              : {}),
          },
        ],
      },
    ];
  });
}

function blocks(text: string): JSONContent[] {
  return mrkdwnBlocks(text).map((block) => {
    if (block.kind === 'list')
      return {
        type: 'bulletList',
        content: block.items.map((item) => ({
          type: 'listItem',
          content: [{ type: 'paragraph', content: inline(item) }],
        })),
      };
    const paragraph = { type: 'paragraph', content: inline(block.text) };
    return block.kind === 'quote'
      ? { type: 'blockquote', content: [paragraph] }
      : paragraph;
  });
}

export function mrkdwnDocument(text: string): JSONContent {
  const content = text
    .split(/(```[\s\S]*?```)/g)
    .flatMap((part): JSONContent[] => {
      if (part.startsWith('```')) {
        const code = part.slice(3, -3).replace(/^\n|\n$/g, '');
        return [
          {
            type: 'codeBlock',
            content: code ? [{ type: 'text', text: code }] : [],
          },
        ];
      }
      return part ? blocks(part.replace(/^\n|\n$/g, '')) : [];
    });
  // A document needs one block; with none the cursor sits outside any block, and
  // the editor throws as it draws the placeholder or unmounts
  return {
    type: 'doc',
    content: content.length ? content : [{ type: 'paragraph' }],
  };
}

export function serializeMrkdwn(node: JSONContent): string {
  if (node.type === 'slackToken') return String(node.attrs?.source ?? '');
  if (node.type === 'text') {
    let text = node.text ?? '';
    for (const mark of node.marks ?? []) {
      if (mark.type === 'bold') text = `*${text}*`;
      if (mark.type === 'italic') text = `_${text}_`;
      if (mark.type === 'strike') text = `~${text}~`;
      if (mark.type === 'code') text = `\`${text}\``;
      if (mark.type === 'link' && typeof mark.attrs?.href === 'string')
        text =
          text === mark.attrs.href
            ? `<${text}>`
            : `<${mark.attrs.href}|${text}>`;
    }
    return text;
  }
  if (node.type === 'hardBreak') return '\n';
  const separator = [
    'doc',
    'blockquote',
    'bulletList',
    'orderedList',
    'listItem',
  ].includes(node.type ?? '')
    ? '\n'
    : '';
  const text = node.content?.map(serializeMrkdwn).join(separator) ?? '';
  if (node.type === 'listItem') return `• ${text}`;
  if (node.type === 'blockquote')
    return text
      .split('\n')
      .map((line) => `> ${line}`)
      .join('\n');
  if (node.type === 'codeBlock') return `\`\`\`\n${text}\n\`\`\``;
  return text;
}
